<template>
  <div class="contract-invoice">
    <div class="contract-invoice__header">
      <h2 class="contract-invoice__header-title">{{i18n.cont_invoices()}}</h2>
      <md-button  v-if="isRequestor"
                  class="md-primary"
                  :disabled="contractCompleted"
                  @click="isCreatingInvoice = true">{{i18n.cont_create_invoice()}}</md-button>
    </div>
    <div class="contract-invoice__wrapper">
      <template v-if="list.length">
        <div class="contract-invoice__row-header">
          <div class="contract-invoice__number-column">{{ i18n.cont_invoice_number() }}</div>
          <div class="contract-invoice__state-column">{{ i18n.lbl_state() }}</div>
          <div class="contract-invoice__type-column">{{ i18n.lbl_request_type() }}</div>
          <div class="contract-invoice__date-column">{{ i18n.lbl_date() }}</div>
          <div class="contract-invoice__funds-column">{{ i18n.lbl_funds() }}</div>
          <div class="contract-invoice__button-column"><!--Button--></div>
        </div>
        <template v-for="(item, i) in list">
          <div class="contract-invoice__row"
              :class="{'contract-invoice__row--active': isSelected(i)}"
              :key="i">
            <div class="contract-invoice__item">
              <div class="contract-invoice__number-column">{{ item.invoiceNumber  }}</div>
              <div class="contract-invoice__state-column">{{ item.state === REQUEST_STATES_STR.permanentlyRejected ? REQUEST_STATES_STR.rejected : item.state.split('_').join(' ') }}</div>
              <div class="contract-invoice__type-column">{{ isRequestor ? i18n.cont_type_outgoing() : i18n.cont_type_incoming()  }}</div>
              <div class="contract-invoice__date-column">{{ i18n.d(item.createdAt) }}</div>
              <div class="contract-invoice__funds-column">{{ roundCurrency(item.amount, item.asset) }} {{ item.asset }} </div>
              <div class="contract-invoice__button-column">
                <md-button class="contract-invoice__open-details-btn md-icon-button"
                           @click="toggleDetails(i)" >
                  <md-icon v-if="isSelected(i)">keyboard_arrow_up</md-icon>
                  <md-icon v-else>keyboard_arrow_down</md-icon>
                </md-button>
              </div>
            </div>
            <div class="contract-invoice__item-details" v-if="isSelected(i)" :key="'selected-'+i">
              <div class="md-title contract-invoice__title">{{i18n.cont_invoice_information()}}</div>
              <div class="contract-invoice__document-details-row">
                <div class="contract-invoice__document-name">
                  <h3>{{i18n.lbl_documents()}}</h3>
                </div>
                <div class="contract-invoice__document-submitter">
                  <h3>{{i18n.lbl_submitted_by()}}</h3>
                </div>
                <div class="contract-invoice__document-type">
                  <h3>{{i18n.lbl_type()}}</h3>
                </div>
                <div class="contract-invoice__documents-view-btn">
                  <!-- open file button -->
                </div>
              </div>
              <div class="contract-invoice__document-details-row">
                <div class="contract-invoice__document-name">
                  <i class="document-icon material-icons">note</i>
                  <p>{{item.invoiceFileName}}</p>
                </div>
                <div class="contract-invoice__document-submitter">
                  <company-name-getter :id="item.requestor" />
                </div>
                <div class="contract-invoice__document-type">
                  <p>{{item.invoiceFileType}}</p>
                </div>
                <div class="contract-invoice__documents-view-btn">
                  <a :href='item.invoiceFileUrl' target='_blank'>{{i18n.lbl_open()}}</a>
                </div>
              </div>
            <detail-row :prop="i18n.lbl_note()" :value="item.note"
                                              :alignLeft="true"
                                              :colWidth="[15, 85]"/>
            <div class="contract-invoice__reject-details"
                v-if="item.state === REQUEST_STATES_STR.permanentlyRejected">
              <h3 class="contract-invoice__reject-details-title">{{i18n.lbl_reject_reason()}}</h3>
              {{item.rejectReason}}
            </div>
              <md-card-actions class="dispute-btn-wrapper">
                <md-button class="md-primary"
                          v-if="!isRequestor && item.state === REQUEST_STATES_STR.pending"
                          :disabled="isPending"
                          @click="rejectSelectedRequest(item)">{{ i18n.lbl_reject() }}</md-button>
                <md-button class="md-primary"
                          v-if="!isRequestor && item.state === REQUEST_STATES_STR.pending"
                          :disabled="isPending"
                          @click="confirmInvoiceRequest(item)">{{ i18n.lbl_confirm() }}</md-button>
              </md-card-actions>
            </div>
          </div>
        </template>
        <template v-if="!isLoaded">
          <div class="contract-invoice__btn-outer">
            <md-button @click="more" :disabled="isLoading">{{ i18n.lbl_view_more() }}</md-button>
          </div>
        </template>
      </template>
      <template v-else>
        <div class="contract-invoice__no-invoices">
          <md-icon class="md-size-4x">trending_up</md-icon>
          <h2>{{ i18n.cont_no_invoice_requests() }}</h2>
          <p>{{ i18n.cont_no_invoice_requests_desc() }}</p>
        </div>
      </template>
    </div>

    <modal v-if="isCreatingInvoice"
           @close-request="isCreatingInvoice = false"
           maxWidth="1200px">
      <create-invoice :contractId="contractId"
                      :customerId="customerId"
                      :invoiceAsset="currentAsset"
                      @asset-change="setCurrentAsset"
                      @close-request="isCreatingInvoice = false"/>
    </modal>
    <modal v-if="isRejectingRequest"
           @close-request="isRejectingRequest = false"
           maxWidth="800px">
      <reject-form @submit-reject-message="rejectInvoiceRequest"
                   @close-request="isRejectingRequest = false"/>
    </modal>
  </div>
</template>

<script>
import get from 'lodash/get'
import { mapGetters, mapActions } from 'vuex'
import { i18n } from '@js/i18n'
import {
  documentTypes,
  ACCOUNT_TYPES,
  REQUEST_STATES_STR,
  CONTRACT_STATES,
  PAYMENT_FEE_SUBTYPES
} from '@const'

import { vuexTypes } from '@vuex/types'
import { ErrorHandler } from '@js/errors/error_handler'
import { EventDispatcher } from '@js/events/event_dispatcher'
import FormMixin from '@com/mixins/form.mixin'

import DetailRow from '@vue/app/common/Detail.Row'
import CompanyNameGetter from '@vue/app/common/CompanyNameGetter'
import Modal from '@vue/app/common/Modal'

import CreateInvoice from './modals/Contracts.CreateInvoice'
import RejectForm from './modals/Contracts.RejectForm'

import { contractsService } from '@api/contracts.service'
import { feeService } from '@api/fees.service'

import CurrencyRound from '@com/mixins/currency.round.mixin'
import {errors} from '@js/errors/factory'

export default {
  components: {
    DetailRow,
    CreateInvoice,
    RejectForm,
    Modal,
    CompanyNameGetter },
  props: ['contractId',
    'customerId',
    'requestorId',
    'contractCompleted'],
  mixins: [FormMixin, CurrencyRound],
  data: _ => ({
    i18n,
    documentTypes,
    isLoading: false,
    currentAsset: null,
    index: -1,
    ACCOUNT_TYPES,
    REQUEST_STATES_STR,
    CONTRACT_STATES,
    isCreatingInvoice: false,
    isAppealingEscrow: false,
    isStartingDispute: false,
    isRejectingRequest: false,
    selectedRequest: null
  }),

  async created () {
    await this.loadList(this.contractId)
    await this.loadBalances()
    this.setCurrentAsset()
  },

  computed: {
    ...mapGetters([
      vuexTypes.accountId,
      vuexTypes.invoicesRequests,
      vuexTypes.accountBalances,
      vuexTypes.factorData
    ]),
    list () {
      return get(this.invoicesRequests, 'records', [])
    },
    isLoaded () {
      return get(this.invoicesRequests, 'isLoaded')
    },
    isRequestor () {
      return this.requestorId === this.accountId
    }
  },

  methods: {
    ...mapActions({
      loadList: vuexTypes.GET_USER_INVOICES_REQUESTS,
      loadNext: vuexTypes.NEXT_USER_INVOICES_REQUESTS,
      loadBalances: vuexTypes.GET_ACCOUNT_BALANCES,
      updateTwoFactorData: vuexTypes.SET_TWO_FACTOR_STATE
    }),

    toggleDetails (index) {
      this.index = this.index === index ? -1 : index
    },

    isSelected (i) {
      return this.index === i
    },

    async more () {
      this.isLoading = true
      try {
        await this.loadNext()
      } catch (e) {
        console.error(e)
        EventDispatcher.dispatchShowErrorEvent(i18n.th_failed_to_load_tx())
      }
      this.isLoading = false
    },

    async confirmPayment () {
      this.disable()
      try {
        await contractsService.confirmCompleted({ contractID: this.contractId })
        EventDispatcher.dispatchShowSuccessEvent(i18n.cont_contract_confirmed())
      } catch (e) {
        console.error(e)
        ErrorHandler.processUnexpected(e)
      }
      this.enable()
    },
    async confirmInvoiceRequest (request) {
      this.disable()

      try {
        const billPayDetails = await this.loadBillPayDetails(request)
        await contractsService.confirmInvoiceRequest(request, billPayDetails)
        await this.loadList(this.contractId)
        EventDispatcher.dispatchShowSuccessEvent(i18n.cont_invoice_confirmed())
      } catch (error) {
        if (error instanceof errors.TFAExceededCodeError) {
          EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
        } else {
          ErrorHandler.processUnexpected(error)
        }
      }

      this.enable()
    },

    rejectSelectedRequest (request) {
      this.selectedRequest = request
      this.isRejectingRequest = true
    },
    async rejectInvoiceRequest (rejectReason) {
      this.disable()

      try {
        const billPayDetails = await this.loadBillPayDetails(this.selectedRequest)
        await contractsService.rejectInvoiceRequest(this.selectedRequest, billPayDetails, rejectReason)
        await this.loadList(this.contractId)
        this.isRejectingRequest = false
        EventDispatcher.dispatchShowSuccessEvent(i18n.cont_invoice_rejected())
      } catch (error) {
        if (error instanceof errors.TFAExceededCodeError) {
          EventDispatcher.dispatchShowErrorEvent(i18n.tfa_to_many_attempts())
        } else {
          ErrorHandler.processUnexpected(error)
        }
      }

      this.enable()
    },

    async loadBillPayDetails (request) {
      const feeData = { sourceFee: {}, destinationFee: {} }
      const [ senderFees, recipientFees ] = await Promise.all([
        feeService.loadPaymentFeeByAmount(request.asset, request.amount),
        feeService.loadPaymentFeeByAmount(request.asset, request.amount, request.requestor, PAYMENT_FEE_SUBTYPES.incoming)
      ])
      feeData.sourceFee.fixedFee = senderFees.fixed
      feeData.sourceFee.maxPaymentFee = senderFees.percent
      feeData.sourceFee.feeAsset = senderFees.feeAsset
      feeData.destinationFee.fixedFee = recipientFees.fixed
      feeData.destinationFee.maxPaymentFee = recipientFees.percent
      feeData.destinationFee.feeAsset = recipientFees.feeAsset
      feeData.sourcePaysForDest = true
      return {
        sourceBalanceId: request.payer,
        destination: request.receiver,
        amount: request.amount,
        subject: '',
        feeData
      }
    },
    setCurrentAsset (value) {
      this.currentAsset = value || Object.keys(this.accountBalances)[0] || null
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "~scss/mixins";
  @import "~scss/variables";

  .contract-invoice {
    margin-top: 1.5rem;
    background-color: $col-background-content;
    // md-card styles
    border-radius: 2px;
    box-shadow: 0px 2px 4px 0 rgba(0, 0, 0, 0.08);
  }
  .contract-invoice__open-details-btn {
    float: right;
    & > .md-ripple > .md-button-content > .md-icon{
      color: $col-text-bright;
    }
  }

  .contract-invoice__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .contract-invoice__header-title {
    padding: 1.5rem;
  }

  .contract-invoice__wrapper {
    display: flex;
    flex-direction: column;
    background-color: $col-background;
  }

  .contract-invoice__item,
  .contract-invoice__row-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1.5rem;
 /*   background-color: $col-content-block;*/
  }

  .contract-invoice__row-header,
  .contract-invoice__row {
    border-bottom: 1px solid $col-background;
    color: $col-text-bright;
  }

  .contract-invoice__row-header {
    color: $col-text-bright;
    height: 2.5rem;
    box-sizing: content-box;
  }

  .contract-invoice__row--active {
    margin: 0.5rem 0;
  }

  .contract-invoice__item-details {
    padding: 0 1.5rem 1rem;
/*    background-color: $col-content-block;*/
  }

  .contract-invoice__number-column { width: 16.66% }
  .contract-invoice__state-column { width: 16.66% }
  .contract-invoice__type-column { width: 13.66% }
  .contract-invoice__date-column { width: 13% }
  .contract-invoice__funds-column { width: 15% }
  .contract-invoice__disputes-column { width: 10% }
  .contract-invoice__button-column { width: 5% }

  .contract-invoice__title {
    margin-bottom: 1rem;
  }

  .contract-invoice__document-details-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .contract-invoice__document-name {
    width: 65%;
    display: flex;
    align-items: center;
  }

  .contract-invoice__document-submitter {
    width: 20%;
    text-align: center;
  }

  .contract-invoice__document-type {
    width: 10%;
    text-align: center;
  }

  .contract-invoice__documents-view-btn {
    text-align: center;
    width: 5%;
  }
  .document-icon {
   /* font-size: $material-icon-big-but-not-so-big;*/
    margin-right: .5rem;
  }

  .dispute-wrapper {
    margin-bottom: 1rem;
  }

  .dispute-btn-wrapper {
    margin-right: -1.5rem;
  }

  .contract-invoice__no-invoices {
    display: flex;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    padding: 1.5rem 0;
/*    background-color: $col-content-block;*/
  }

  .contract-invoice__document-header-row { display: flex; }
  .contract-invoice__reject-details { margin-top: 1rem; }

  .contracts {
    z-index: 1;
  }
</style>



// WEBPACK FOOTER //
// src/vue/app/contracts/index/Contracts.Invoice.vue